import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, UsersIcon, ArrowDownIcon, ChevronDownIcon } from 'lucide-react';
import { ColumviLogo } from './ColumviLogo';
import { ColumviBackground } from './ColumviBackground';
interface DeepDiveSlideProps {
  onFinish?: () => void;
}
export function DeepDiveSlide({
  onFinish
}: DeepDiveSlideProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return <ColumviBackground showWave={false} data-id="element-106">
      <div className="flex flex-col h-full w-full p-6 relative overflow-y-auto" data-id="element-107">
        {/* Logo */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6
      }} className="absolute top-6 left-6 z-20" data-id="element-108">
          <ColumviLogo size="sm" variant="light" data-id="element-109" />
        </motion.div>

        {/* Content */}
        <div className="flex-1 flex flex-col z-10 w-full mt-16" data-id="element-110">
          <motion.h2 className="text-white text-2xl leading-tight mb-6" style={{
          fontFamily: 'var(--font-gotham)',
          fontWeight: 700
        }} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-111">
            Observed across patient subgroups
          </motion.h2>

          {/* Visual - Vertical Layout */}
          <motion.div className="flex flex-col items-center justify-center relative py-4 mb-6" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} data-id="element-112">
            {/* Connection Lines SVG - Vertical */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
            zIndex: 0
          }} data-id="element-113">
              <motion.path d="M 120 60 C 120 120, 200 120, 200 180" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeDasharray="4 4" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1.5,
              delay: 1
            }} data-id="element-114" />
              <motion.path d="M 280 60 C 280 120, 200 120, 200 180" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeDasharray="4 4" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1.5,
              delay: 1.2
            }} data-id="element-115" />
            </svg>

            {/* Subgroup Nodes - Horizontal Row */}
            <div className="flex justify-between w-full px-4 z-10 mb-12" data-id="element-116">
              {/* Group 1 */}
              <motion.div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3 flex flex-col items-center text-center w-[45%]" initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} data-id="element-117">
                <div className="flex gap-1 mb-2 text-[#2D6A35]" data-id="element-118">
                  <UserIcon size={20} data-id="element-119" />
                  <UserIcon size={20} className="opacity-50" data-id="element-120" />
                </div>
                <span className="text-white/90 text-xs font-medium" style={{
                fontFamily: 'var(--font-gotham)'
              }} data-id="element-121">
                  Early / Late Relapse
                </span>
              </motion.div>

              {/* Group 2 */}
              <motion.div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3 flex flex-col items-center text-center w-[45%]" initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.7
            }} data-id="element-122">
                <div className="flex gap-1 mb-2 text-[#2D6A35]" data-id="element-123">
                  <UsersIcon size={20} data-id="element-124" />
                </div>
                <span className="text-white/90 text-xs font-medium" style={{
                fontFamily: 'var(--font-gotham)'
              }} data-id="element-125">
                  Younger / Older
                </span>
              </motion.div>
            </div>

            {/* Outcome Node */}
            <motion.div className="z-10 bg-gradient-to-br from-[#1E4D2B] to-[#163D22] border border-[#2D6A35] rounded-2xl p-5 shadow-2xl flex flex-col items-center text-center w-48" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 1.5
          }} data-id="element-126">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2 border border-white/20" data-id="element-127">
                <ArrowDownIcon size={20} className="text-white" data-id="element-128" />
              </div>
              <span className="text-white text-base font-bold leading-tight" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-129">
                Durable Response
              </span>
            </motion.div>
          </motion.div>

          {/* Insight Callout */}
          <motion.div className="border-l-2 border-[#E76F24] pl-3 py-2 mb-6 bg-gradient-to-r from-[#E76F24]/10 to-transparent" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-130">
            <p className="text-white text-sm font-medium leading-snug" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-131">
              Expands curative intent beyond traditionally selected patients
            </p>
          </motion.div>

          {/* Expandable Section */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mb-auto" data-id="element-132">
            <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center text-white/60 hover:text-white transition-colors text-xs font-medium uppercase tracking-wider w-full justify-center py-2" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-133">
              Explore subgroups
              <motion.div animate={{
              rotate: isExpanded ? 180 : 0
            }} transition={{
              duration: 0.3
            }} className="ml-2" data-id="element-134">
                <ChevronDownIcon size={14} data-id="element-135" />
              </motion.div>
            </button>

            <AnimatePresence data-id="element-136">
              {isExpanded && <motion.div initial={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} animate={{
              opacity: 1,
              height: 'auto',
              marginTop: 8
            }} exit={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} className="overflow-hidden" data-id="element-137">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-white/70 text-xs leading-relaxed" style={{
                fontFamily: 'var(--font-gotham)'
              }} data-id="element-138">
                    Consistent OS benefit was observed across key prespecified
                    subgroups, including those with primary refractory disease,
                    prior CAR T-cell therapy, and bulky disease.
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Continue Button */}
        <motion.button onClick={onFinish} className="w-full py-3.5 rounded-xl text-white text-sm font-semibold transition-all duration-300 mt-4" style={{
        fontFamily: 'var(--font-gotham)',
        background: '#E76F24',
        boxShadow: '0 4px 15px rgba(231,111,36,0.3)'
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} whileTap={{
        scale: 0.98
      }} data-id="element-139">
          See clinical resources →
        </motion.button>

        {/* Footer */}
        <div className="mt-3 text-center text-white/30 text-[10px]" style={{
        fontFamily: 'var(--font-gotham)'
      }} data-id="element-140">
          Source: Sales Aid p.1, p.12–14
        </div>
      </div>
    </ColumviBackground>;
}